import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { NavLink } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import {
  X,
  LayoutDashboard,
  BookOpen,
  Users,
  GraduationCap,
  Calendar,
  CalendarDays,
  DollarSign,
  UserCheck,
  ChevronDown,
  ChevronRight,
  BarChart3,
  FileText,
  Shield,
  Bell
} from 'lucide-react';

const Sidebar = ({ isOpen, onClose, currentPath }) => {
  const { user } = useAuth();
  const [expandedItems, setExpandedItems] = useState({});

  const navigationItems = [
    {
      name: 'Dashboard',
      href: '/dashboard',
      icon: LayoutDashboard,
      roles: ['admin', 'teacher']
    },
    {
      name: 'Students',
      href: '/students',
      icon: Users,
      roles: ['admin', 'teacher']
    },
    {
      name: 'Parents',
      href: '/parents',
      icon: UserCheck,
      roles: ['admin', 'teacher']
    },
    {
      name: 'Academic Operations',
      href: '/classes',
      icon: BookOpen,
      roles: ['admin', 'teacher'],
      subItems: [
        { name: 'Class Management', href: '/classes', icon: BookOpen, roles: ['admin'] },
        { name: 'Subjects', href: '/subjects', icon: BookOpen },
        { name: 'Exam Schedules', href: '/exams', icon: FileText },
        { name: 'Results & Reports', href: '/exam-results', icon: BarChart3 },
        { name: 'Academic Calendar', href: '/calendar', icon: CalendarDays, roles: ['admin'] }
      ]
    },

    {
      name: 'Notices',
      href: '/notices',
      icon: Bell,
      roles: ['admin', 'teacher']
    },
    {
      name: 'Fees',
      href: '/fees',
      icon: DollarSign,
      roles: ['admin']
    },
    {
      name: 'Attendance System',
      href: '/attendance',
      icon: Calendar,
      roles: ['admin', 'teacher']
    },
    {
      name: 'Administration',
      href: '/users',
      icon: Shield,
      roles: ['admin']
    }
  ];

  const handleNavClick = () => {
    if (window.innerWidth < 1024) {
      onClose();
    }
  };

  const toggleExpanded = (itemName) => {
    setExpandedItems(prev => ({
      ...prev,
      [itemName]: !prev[itemName]
    }));
  };

  const isItemActive = (item, currentPath) => {
    if (item.href === currentPath) return true;
    if (item.subItems) {
      return item.subItems.some(subItem => subItem.href === currentPath);
    }
    return false;
  };

  return (
    <>
      {/* Mobile backdrop */}
      {isOpen && (
        <div
          className="fixed inset-0 bg-gray-600 bg-opacity-75 z-40 lg:hidden"
          onClick={onClose}
        />
      )}

      {/* Sidebar */}
      <div className={`
        fixed inset-y-0 left-0 z-50 w-64 bg-white shadow-lg transform transition-transform duration-300 ease-in-out lg:translate-x-0 lg:static lg:inset-0 flex flex-col h-full
        ${isOpen ? 'translate-x-0' : '-translate-x-full'}
      `}>
        <div className="flex items-center justify-between h-16 px-6 border-b border-gray-200">
          <div className="flex items-center space-x-2">
            <div className="h-8 w-8 rounded-lg flex items-center justify-center" style={{ backgroundColor: 'var(--primary-color, #4f46e5)' }}>
              <GraduationCap className="h-5 w-5 text-white" />
            </div>
            <span className="text-xl font-bold text-gray-900">Adhyayan Axis</span>
          </div>

          <button
            onClick={onClose}
            className="lg:hidden p-1 rounded-md text-gray-400 hover:text-gray-600 hover:bg-gray-100"
          >
            <X className="h-5 w-5" />
          </button>
        </div>



        {/* Navigation */}
        <nav className="mt-6 px-3 flex-grow overflow-y-auto">
          <div className="space-y-2 pb-4">
            {navigationItems.map((item) => {
              // Check if user has access to this navigation item
              if (!item.roles.includes(user?.userRole)) {
                return null;
              }

              const Icon = item.icon;
              const isActive = isItemActive(item, currentPath);
              const isExpanded = expandedItems[item.name];
              const hasSubItems = item.subItems && item.subItems.length > 0;
              
              return (
                <div key={item.name}>
                  {/* Main Navigation Item */}
                  {hasSubItems ? (
                    <button
                      onClick={() => toggleExpanded(item.name)}
                      className="w-full flex items-center justify-between px-3 py-3 text-left text-sm font-medium rounded-lg transition-colors text-gray-700 hover:bg-gray-100"
                      style={isActive ? {
                        backgroundColor: 'color-mix(in srgb, var(--primary-color, #4f46e5) 10%, white)',
                        color: 'var(--primary-color, #4f46e5)',
                        borderLeft: '4px solid var(--primary-color, #4f46e5)'
                      } : {}}
                    >
                      <div className="flex items-center">
                        <Icon className="h-5 w-5 mr-3 flex-shrink-0" />
                        <span className="truncate">{item.name}</span>
                      </div>
                      {isExpanded ? (
                        <ChevronDown className="h-4 w-4 flex-shrink-0" />
                      ) : (
                        <ChevronRight className="h-4 w-4 flex-shrink-0" />
                      )}
                    </button>
                  ) : (
                    <NavLink
                      to={item.href}
                      onClick={handleNavClick}
                      className="flex items-center px-3 py-3 text-sm font-medium rounded-lg transition-colors text-gray-700 hover:bg-gray-100"
                      style={isActive ? {
                        backgroundColor: 'color-mix(in srgb, var(--primary-color, #4f46e5) 10%, white)',
                        color: 'var(--primary-color, #4f46e5)',
                        borderLeft: '4px solid var(--primary-color, #4f46e5)'
                      } : {}}
                    >
                      <Icon className="h-5 w-5 mr-3 flex-shrink-0" />
                      <span className="truncate">{item.name}</span>
                    </NavLink>
                  )}

                  {/* Sub Items */}
                  {hasSubItems && isExpanded && (
                    <div className="ml-4 mt-1 space-y-1">
                      {item.subItems.map((subItem) => {
                        // Check if user has access to this sub-item
                        if (subItem.roles && !subItem.roles.includes(user?.userRole)) {
                          return null;
                        }
                        
                        const SubIcon = subItem.icon;
                        const isSubActive = currentPath === subItem.href;
                        
                        return (
                          <NavLink
                            key={subItem.name}
                            to={subItem.href}
                            onClick={handleNavClick}
                            className="flex items-center px-3 py-2 text-sm font-medium rounded-lg transition-colors text-gray-600 hover:bg-gray-50 hover:text-gray-900"
                            style={isSubActive ? {
                              backgroundColor: 'color-mix(in srgb, var(--primary-color, #4f46e5) 5%, white)',
                              color: 'var(--primary-color, #4f46e5)',
                              borderLeft: '2px solid var(--primary-color, #4f46e5)'
                            } : {}}
                          >
                            <SubIcon className="h-4 w-4 mr-3 flex-shrink-0" />
                            <span className="truncate">{subItem.name}</span>
                          </NavLink>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </nav>

        {/* Footer */}
        <div className="mt-auto p-4 border-t border-gray-200">
          <div className="text-center">
            <p className="text-xs text-gray-500">
              EdU Platform v1.0.0
            </p>
            <p className="text-xs text-gray-400 mt-1">
              Built for Education
            </p>
          </div>
        </div>
      </div>
    </>
  );
};

Sidebar.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  currentPath: PropTypes.string,
};

Sidebar.defaultProps = {
  currentPath: '',
};

export default Sidebar;
